import { memo, useEffect } from 'react'
import { sendEvent } from '@core/analytics'
import { Logger } from '@core/logger'
import { DIALOG_IDS, modalStore, type TDialogId } from '@core/modal'
import { queryClient } from '@core/query'
import { useActions, useValues } from '@core/store'
import { authIntentStore, INTENT_TTL_MS } from '@features/auth'
import { useCurrentUser } from '@features/session'
import { FollowQueryKey, useFollow } from '@features/follow'
import { likedAudioIdsQuery, useFirstLikeDialog, useLikeAudio } from '@features/audio-like'
import { useDownloadDialog } from '@features/audio-download'
import { useCollectionDownload } from '@features/collection'
import { checkoutStore, hasPurchaseReturnInUrl } from '@features/checkout'
import { runAuthIntent } from './authIntentResolvers'

const PURCHASE_CHAIN_DIALOGS: TDialogId[] = [
    DIALOG_IDS.paywall,
    DIALOG_IDS.checkout,
    DIALOG_IDS.purchaseSuccess,
]

/**
 * Replays a guest's pending action after login. Mounted once in the app layer (like `ModalHost`),
 * so it may compose feature hooks across boundaries. Fires when the session id flips to a real user
 * and a fresh intent is present, then clears it. Replaces audio-web's window-CustomEvent dispatch.
 *
 * A registration ends on the post-registration paywall, and `modalStore` holds one dialog at a time —
 * replaying straight away would have the two knocking each other out.
 */
export const AuthIntentRunner = memo(() => {
    const { id: userId, username: myUsername } = useCurrentUser()
    const [intent, setAt, regPaywall, activeDialog, isCheckoutRedirectPending] = useValues([
        authIntentStore.props.getIntent,
        authIntentStore.props.getSetAt,
        authIntentStore.props.getRegPaywall,
        modalStore.props.getActiveDialog,
        checkoutStore.props.getIsRedirectPending,
    ])
    const { clearIntent } = useActions({ clearIntent: authIntentStore.actions.clearIntent })
    const follow = useFollow()
    const likeAudio = useLikeAudio()
    const firstLikeDialog = useFirstLikeDialog()
    const { resume: resumeDownload } = useDownloadDialog()
    const { download: downloadCollection } = useCollectionDownload()

    useEffect(() => {
        if (userId === 0 || !intent) {
            return
        }

        // Armed = the paywall is about to open; open = the user is still on it. The url marker covers
        // the gap before it opens: `PurchaseSuccessRunner` waits for hydration to finish, so on the
        // first commit after a purchase return no dialog is open yet and the replay would be knocked
        // out by the success dialog a moment later.
        if (
            regPaywall ||
            isCheckoutRedirectPending ||
            hasPurchaseReturnInUrl() ||
            (activeDialog !== null && PURCHASE_CHAIN_DIALOGS.includes(activeDialog))
        ) {
            return
        }

        if (Date.now() - setAt <= INTENT_TTL_MS) {
            runAuthIntent(intent, {
                myUsername,
                follow: vars => follow.mutate(vars),
                like: (audioId, placement) =>
                    likeAudio.mutate(audioId, {
                        onSuccess: () => {
                            sendEvent({
                                name: 'like_click',
                                payload: {
                                    audio: audioId,
                                    params__key: ['player_placement'],
                                    params__str_value: [placement],
                                },
                            })

                            if (!firstLikeDialog.hasSeen) {
                                firstLikeDialog.open()
                            }
                        },
                    }),
                resumeDownload,
                downloadCollection,
                isLiked: async audioId =>
                    (await queryClient.ensureQueryData(likedAudioIdsQuery())).includes(audioId),
                isFollowing: username =>
                    queryClient.getQueryData<boolean>(FollowQueryKey.followCheck(username)) ??
                    false,
            }).catch(error => Logger.error('Auth intent replay error', { error }))
        }

        clearIntent()
        // Fire on login (userId) / intent change; the callbacks are stable enough for a one-shot replay.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, intent, setAt, regPaywall, activeDialog, isCheckoutRedirectPending])

    return null
})
